import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../styles/AdminSubscriptionsTable.css';
import ConfirmationModal from './ConfirmationModal';
import SuccessModal from './SuccessModal';
import ErrorModal from './ErrorModal';
import PaymentModal from './PaymentModal';
import SubscriptionPlanChart from './SubscriptionPlanChart';

const AdminSubscriptionsTable = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [filteredSubscriptions, setFilteredSubscriptions] = useState([]);
  const [statusFilter, setStatusFilter] = useState('todos');
  const [textFilter, setTextFilter] = useState('');
  const [statusFilteredData, setStatusFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [paymentDays, setPaymentDays] = useState(30);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('efectivo');
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [showDeleteFinalConfirmModal, setShowDeleteFinalConfirmModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Función para obtener suscripciones de administradores verificados
  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get('/api/subscriptions/admin-verified', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      setSubscriptions(response.data);
      setFilteredSubscriptions(response.data);
    } catch (error) {
      console.error('Error fetching admin subscriptions:', error);
      setError('Failed to load subscription data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Cargar suscripciones al montar el componente
  useEffect(() => {
    fetchSubscriptions();
  }, []);
  
  // Filtrar suscripciones por estado
  useEffect(() => {
    if (statusFilter === 'todos') {
      setStatusFilteredData(subscriptions);
    } else {
      const filtered = subscriptions.filter(subscription => 
        subscription.status.toLowerCase() === statusFilter.toLowerCase()
      );
      setStatusFilteredData(filtered);
    }
  }, [statusFilter, subscriptions]);

  // Filtrar por texto después de filtrar por estado
  useEffect(() => {
    if (!textFilter.trim()) {
      // Si no hay texto de búsqueda, usar solo el filtro de estado
      setFilteredSubscriptions(statusFilteredData);
    } else {
      // Filtrar los datos ya filtrados por estado
      const searchTerm = textFilter.toLowerCase();
      const filtered = statusFilteredData.filter(subscription => 
        subscription.users.full_name.toLowerCase().includes(searchTerm) ||
        subscription.users.email.toLowerCase().includes(searchTerm) ||
        (subscription.business_name && subscription.business_name.toLowerCase().includes(searchTerm))
      );
      setFilteredSubscriptions(filtered);
    }
  }, [textFilter, statusFilteredData]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'No establecida';
    
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  // Format subscription status for display
  const formatStatus = (status) => {
    if (!status) return 'No disponible';
    
    const statusMap = {
      'vigente': 'Vigente',
      'trial': 'Prueba',
      'prueba': 'Prueba', // Añadido para manejar 'Prueba' como equivalente a 'trial'
      'vencido': 'Vencido',
      'pending': 'Pendiente',
      'suspendido': 'Suspendido'
    };
    
    return statusMap[status.toLowerCase()] || status;
  };

  // Format currency for display
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '$0.00';
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  // Handle update status
  const handleUpdateStatus = async (id, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      const newStatus = prompt('Ingrese el nuevo estado (vigente, trial, vencido, pending, suspendido):', currentStatus);
      
      if (!newStatus || newStatus === currentStatus) {
        return; // User cancelled or no change
      }
      
      await axios.put(`/api/subscriptions/${id}/status`, { status: newStatus }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // Update local state
      setSubscriptions(subscriptions.map(sub => 
        sub.id === id ? { ...sub, status: newStatus.toLowerCase() } : sub
      ));
      
      alert('Estado de suscripción actualizado exitosamente');
    } catch (error) {
      console.error('Error updating subscription status:', error);
      alert('Error al actualizar el estado de la suscripción');
    }
  };
  
  // Open renew subscription modal
  const openRenewModal = (subscription) => {
    setSelectedSubscription(subscription);
    // Establecer valores predeterminados
    setPaymentDays(30);
    setPaymentAmount(subscription.monthly_fee || '');
    setPaymentMethod('efectivo');
    setShowRenewModal(true);
  };

  // Handle renew subscription
  const handleRenewSubscription = async () => {
    try {
      if (!selectedSubscription) return;
      
      // Validaciones
      if (paymentAmount === '' || isNaN(parseFloat(paymentAmount)) || parseFloat(paymentAmount) < 0) {
        setErrorMessage('Por favor ingrese un monto válido');
        setShowErrorModal(true);
        return;
      }
      
      if (paymentDays <= 0 || isNaN(paymentDays)) {
        setErrorMessage('Por favor ingrese un número de días válido');
        setShowErrorModal(true);
        return;
      }
      
      const token = localStorage.getItem('token');
      
      // Preparar datos para la renovación
      const renewalData = {
        subscription_id: selectedSubscription.id,
        days: parseInt(paymentDays),
        amount: parseFloat(paymentAmount),
        payment_method: paymentMethod
      };
      
      // Llamar a la API para renovar la suscripción
      const response = await axios.put(`/api/subscriptions/${selectedSubscription.id}/renew`, {
        days: parseInt(paymentDays),
        amount: parseFloat(paymentAmount),
        paymentMethod: paymentMethod
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // El backend ahora solo devuelve información del pago, no de la suscripción actualizada
      // El trigger de Supabase se encarga de actualizar la suscripción automáticamente
      const paymentResponse = response.data;
      
      // Close confirmation modal
      setShowRenewModal(false);
      
      // Obtener el nombre del método de pago para mostrar
      const paymentMethodName = {
        'tcredito': 'Tarjeta de Crédito',
        'tdebito': 'Tarjeta de Débito',
        'transferencia': 'Transferencia',
        'efectivo': 'Efectivo',
        'paypal': 'PayPal',
        'oxxo': 'OXXO'
      }[paymentMethod] || paymentMethod;
      
      // Mostrar mensaje de éxito
      setSuccessMessage(`Suscripción renovada exitosamente por ${paymentDays} días. Método de pago: ${paymentMethodName}`);
      setShowSuccessModal(true);
      
      // Recargar los datos de suscripciones para reflejar los cambios
      fetchSubscriptions();
    } catch (error) {
      console.error('Error renovando suscripción:', error.response?.data || error.message);
      setShowRenewModal(false);
      setErrorMessage(error.response?.data?.error || 'Error al renovar la suscripción. Por favor, inténtelo de nuevo.');
      setShowErrorModal(true);
    }
  };

  // Handle edit user
  const handleEditUser = (user) => {
    alert(`Funcionalidad para editar usuario ${user.full_name} será implementada próximamente`);
  };

  // Iniciar proceso de eliminación de negocio
  const handleSuspendUser = (subscriptionId, userName) => {
    // Guardar información del usuario y suscripción a eliminar
    setUserToDelete({ subscriptionId, userName });
    // Mostrar el primer modal de confirmación
    setShowDeleteConfirmModal(true);
  };
  
  // Confirmar primera etapa de eliminación
  const handleFirstDeleteConfirmation = () => {
    setShowDeleteConfirmModal(false);
    setShowDeleteFinalConfirmModal(true);
  };
  
  // Cancelar eliminación
  const handleCancelDelete = () => {
    setShowDeleteConfirmModal(false);
    setShowDeleteFinalConfirmModal(false);
    setUserToDelete(null);
  };
  
  // Ejecutar eliminación final del negocio
  const handleFinalDeleteConfirmation = async () => {
    if (!userToDelete) return;
    
    try {
      const token = localStorage.getItem('token');
      
      // Buscar el business_id correspondiente a esta suscripción
      const subscription = subscriptions.find(sub => sub.id === userToDelete.subscriptionId);
      
      if (!subscription) {
        setErrorMessage('No se encontró la suscripción');
        setShowErrorModal(true);
        setShowDeleteFinalConfirmModal(false);
        setUserToDelete(null);
        return;
      }
      
      // Verificar que el business_id exista
      if (!subscription.business_id) {
        setErrorMessage('No se encontró el ID del negocio');
        setShowErrorModal(true);
        setShowDeleteFinalConfirmModal(false);
        setUserToDelete(null);
        return;
      }
      
      // Llamar al endpoint para eliminar el negocio
      // Asegurarse de que el business_id se pase correctamente
      const response = await axios.delete(`/api/businesses/${subscription.business_id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // Verificar la respuesta
      if (response.data && response.data.success) {
        // Actualizar el estado local - eliminar la suscripción de la lista
        setSubscriptions(subscriptions.filter(sub => sub.id !== userToDelete.subscriptionId));
        setFilteredSubscriptions(filteredSubscriptions.filter(sub => sub.id !== userToDelete.subscriptionId));
        
        // Cerrar modal de confirmación
        setShowDeleteFinalConfirmModal(false);
        
        // Mostrar mensaje de éxito
        setSuccessMessage(`Negocio de ${userToDelete.userName} eliminado exitosamente`);
        setShowSuccessModal(true);
      } else {
        throw new Error('La respuesta del servidor no indica éxito');
      }
      
      // Limpiar datos del usuario a eliminar
      setUserToDelete(null);
    } catch (error) {
      console.error('Error eliminando negocio:', error);
      setErrorMessage(`Error al eliminar el negocio: ${error.response?.data?.error || error.message}`);
      setShowErrorModal(true);
      setShowDeleteFinalConfirmModal(false);
      setUserToDelete(null);
    }
  };

  if (loading) {
    return <div className="loading">Cargando datos de suscripciones...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  // Manejar cambio de filtro de estado
  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };

  // Manejar cambio de filtro de texto
  const handleTextFilterChange = (e) => {
    setTextFilter(e.target.value);
  };

  return (
    <div className="admin-subscriptions-section">
      <h2>Control de Suscripciones - Usuarios Admin Verificados</h2>
      
      {/* Modal de confirmación para renovar suscripción */}
      <PaymentModal
        isOpen={showRenewModal}
        title="Renovar Suscripción"
        onClose={() => setShowRenewModal(false)}
        onCancel={() => setShowRenewModal(false)}
        onConfirm={handleRenewSubscription}
        userName={selectedSubscription?.users?.full_name || ''}
        paymentDays={paymentDays}
        setPaymentDays={setPaymentDays}
        paymentAmount={paymentAmount}
        setPaymentAmount={setPaymentAmount}
        paymentMethod={paymentMethod}
        setPaymentMethod={setPaymentMethod}
      />
      
      <SuccessModal
        isOpen={showSuccessModal}
        title="Operación Exitosa"
        message={successMessage}
        onClose={() => {
          setShowSuccessModal(false);
          setSelectedSubscription(null);
        }}
      />
      
      <ErrorModal
        isOpen={showErrorModal}
        title="Error"
        message={errorMessage}
        onClose={() => {
          setShowErrorModal(false);
        }}
      />
      
      <ConfirmationModal
        isOpen={showDeleteConfirmModal}
        title="Confirmar Eliminación"
        message={userToDelete ? `¿Está seguro que desea eliminar el negocio de ${userToDelete.userName}?` : ''}
        onConfirm={handleFirstDeleteConfirmation}
        onCancel={handleCancelDelete}
      />
      
      <ConfirmationModal
        isOpen={showDeleteFinalConfirmModal}
        title="Confirmación Final"
        message="Esta acción es irreversible y eliminará todos los datos asociados al negocio. ¿Estás seguro de continuar?"
        onConfirm={handleFinalDeleteConfirmation}
        onCancel={handleCancelDelete}
      />
      
      {subscriptions.length === 0 ? (
        <p>No hay suscripciones de usuarios admin verificados disponibles</p>
      ) : (
        <>
          <div className="subscription-filters">
            <div className="filter-group">
              <label htmlFor="status-filter">Filtrar por Estado: </label>
              <select 
                id="status-filter" 
                value={statusFilter} 
                onChange={handleStatusFilterChange}
                className="status-filter-select"
              >
                <option value="todos">Todos los Estados</option>
                <option value="trial">Prueba</option>
                <option value="vigente">Vigente</option>
                <option value="vencido">Vencido</option>
              </select>
            </div>
            
            <div className="filter-group">
              <label htmlFor="text-filter">Buscar: </label>
              <input
                type="text"
                id="text-filter"
                value={textFilter}
                onChange={handleTextFilterChange}
                placeholder="Nombre, Email o Negocio"
                className="text-filter-input"
              />
            </div>
          </div>
          
          <div className="table-responsive">
            <table className="admin-subscriptions-table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Email</th>
                  <th>Negocio</th>
                  <th>Plan</th>
                  <th>Estado</th>
                  <th>Vencimiento</th>
                  <th>Cuota Mensual</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredSubscriptions.map((subscription) => (
                  <tr key={subscription.id} className={!subscription.users.alive ? 'user-suspended' : ''}>
                    <td>{subscription.users.full_name}</td>
                    <td>{subscription.users.email}</td>
                    <td>{subscription.business_name}</td>
                    <td>{subscription.type_subscript || 'No especificado'}</td>
                    <td>
                      <span className={`status-badge status-${subscription.status}`}>
                        {formatStatus(subscription.status)}
                      </span>
                    </td>
                    <td>{formatDate(subscription.expiration_date)}</td>
                    <td>{formatCurrency(subscription.monthly_fee)}</td>
                    <td className="action-buttons">
                      <button 
                        className="btn-update-status"
                        onClick={() => openRenewModal(subscription)}
                        disabled={!subscription.users.alive}
                      >
                        Pagos
                      </button>
                      <button 
                        className="btn-edit-user"
                        onClick={() => handleEditUser(subscription.users)}
                        disabled={!subscription.users.alive}
                      >
                        Editar
                      </button>
                      <button 
                        className="btn-suspend-user"
                        onClick={() => handleSuspendUser(subscription.id, subscription.users.full_name)}
                        disabled={!subscription.users.alive}
                      >
                        {subscription.users.alive ? 'Eliminar' : 'Suspendido'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Gráfico de media dona para distribución de planes */}
          <SubscriptionPlanChart subscriptions={subscriptions} />
        </>
      )}
    </div>
  );
};

export default AdminSubscriptionsTable;
